import { lazy, Suspense } from "react";

import digitalOpsLoaderData from "./digital-ops-loader.json";

// The Lennar loading animation the ported screens show while a list is in
// flight — the donor's shared `Loader` (`timmy/packages/react-ui/src/loader`),
// ported as a shared Atlas component because it is shared in the donor: every
// list view draws it through `ListView`'s `loadingIndicator`.
//
// The animation itself is the donor's own asset, vendored beside this file:
// `digital-ops-loader.json`, a 480×480 Lottie of a house being drawn, looping
// at 30fps. Approximating it in CSS would be a different animation, which on a
// branded loader is a parity break rather than an implementation detail — the
// owner's ruling (#250, 2026-08-31) was to port it as it is.
//
// The facts read off the donor:
//
//   - the player fills 65% of its box, capped at 15rem square, centred,
//   - it loops and plays immediately, and is `aria-hidden` — it says nothing,
//   - the accessible half is the wrapper: `role='status'`, labelled "Loading
//     content", with a visually hidden "Loading..." inside it.
//
// **Loaded lazily**, which the donor has no reason to do and this app does: the
// player pulls in `lottie-web`, and a loading animation that costs the first
// paint to download is a loading animation that made the page slower. Nothing
// is drawn until the chunk lands — the scrim over the list is already the
// "something is happening" signal, and the wrapper announces it.

// The **light** build of the player: same renderer, minus lottie-web's
// expression engine — which is a `new Function`/`eval` evaluator this animation
// has no use for and which any future `script-src` policy would refuse.
const Player = lazy(async () => ({
  default: (await import("react-lottie-player/dist/LottiePlayerLight")).default,
}));

/**
 * Which of the donor's two loaders this is.
 *
 * `"list"` is the shared `Loader` above — 65% of its box, capped at 15rem — and
 * is what every list view draws. `"page"` is `timmy/src/components/loading/
 * loading.tsx`, the whole-page one a route shows before it has anything to
 * draw, and it is a **fixed 18rem square** rather than a share of its box: a
 * page loader sized as a fraction of a tall viewport is a different, larger
 * animation on every screen.
 */
export type LoaderSize = "list" | "page";

export function Loader({ size = "list" }: { size?: LoaderSize } = {}) {
  return (
    <div
      role="status"
      aria-label="Loading content"
      className="relative flex h-full w-full items-center justify-center"
    >
      <span className="sr-only">Loading...</span>
      <div className={size === "page" ? "size-72" : "h-[65%] max-h-60 w-[65%] max-w-60"}>
        <Suspense fallback={null}>
          <Player
            loop
            play
            animationData={digitalOpsLoaderData}
            style={{ width: "100%", height: "100%" }}
            aria-hidden="true"
          />
        </Suspense>
      </div>
    </div>
  );
}
